import { cn } from '../../lib/utils';

interface Step {
  label: string;
  description?: string;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  className?: string;
}

const TireIcon = ({ rotation = 0, isActive = false, isCompleted = false }: { rotation?: number; isActive?: boolean; isCompleted?: boolean }) => {
  return (
    <svg
      className={cn(
        'w-full h-full transition-all duration-500 ease-out',
        isActive && 'drop-shadow-lg'
      )}
      style={{
        transform: `rotate(${rotation}deg)`,
        transition: 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
      }}
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outer tire edge */}
      <circle
        cx="40"
        cy="40"
        r="35"
        stroke="currentColor"
        strokeWidth="3"
        fill={isCompleted ? 'currentColor' : 'none'}
        opacity={isCompleted ? '0.2' : '0.95'}
      />

      {/* Inner rim */}
      <circle cx="40" cy="40" r="20" stroke="currentColor" strokeWidth="2.5" fill="none" opacity="0.9"/>

      {/* Center hub */}
      <circle cx="40" cy="40" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.7"/>

      {/* Rim spokes */}
      <g stroke="currentColor" strokeWidth="1.8" opacity="0.6" strokeLinecap="round">
        <line x1="40" y1="20" x2="40" y2="30" />
        <line x1="54" y1="26" x2="47" y2="33" />
        <line x1="54" y1="54" x2="47" y2="47" />
        <line x1="26" y1="54" x2="33" y2="47" />
        <line x1="26" y1="26" x2="33" y2="33" />
      </g>

      {/* Tire tread marks */}
      <g stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" opacity="0.9">
        <line x1="40" y1="5" x2="40" y2="14" />
        <line x1="52" y1="9" x2="49" y2="17" />
        <line x1="62" y1="18" x2="56" y2="24" />
        <line x1="68" y1="30" x2="61" y2="34" />
        <line x1="70" y1="40" x2="63" y2="40" />
        <line x1="68" y1="50" x2="61" y2="46" />
        <line x1="62" y1="62" x2="56" y2="56" />
        <line x1="52" y1="71" x2="49" y2="63" />
        <line x1="40" y1="75" x2="40" y2="66" />
        <line x1="28" y1="71" x2="31" y2="63" />
        <line x1="18" y1="62" x2="24" y2="56" />
        <line x1="12" y1="50" x2="19" y2="46" />
        <line x1="10" y1="40" x2="17" y2="40" />
        <line x1="12" y1="30" x2="19" y2="34" />
        <line x1="18" y1="18" x2="24" y2="24" />
        <line x1="28" y1="9" x2="31" y2="17" />
      </g>

      {/* Completion checkmark or step indicator */}
      {isCompleted ? (
        <path
          d="M 32 40 L 37 45 L 48 34"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      ) : (
        <circle cx="40" cy="40" r="6" fill="currentColor" opacity="0.3"/>
      )}
    </svg>
  );
};

export const Stepper = ({ steps, currentStep, className }: StepperProps) => {
  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isActive = index === currentStep;
          const rotation = index * 90; // Rotate 90 degrees per step

          return (
            <div key={index} className="flex-1 flex items-center">
              <div className="flex flex-col items-center flex-1">
                <div className="flex items-center w-full">
                  <div
                    className={cn(
                      'w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500',
                      isCompleted
                        ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/50 scale-100'
                        : isActive
                        ? 'bg-white text-brand-600 border-3 border-brand-600 shadow-xl shadow-brand-600/30 scale-110'
                        : 'bg-gray-100 text-gray-400 border-2 border-gray-300 scale-95'
                    )}
                  >
                    <TireIcon
                      rotation={rotation}
                      isActive={isActive}
                      isCompleted={isCompleted}
                    />
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 h-1.5 mx-3 relative overflow-hidden rounded-full bg-gray-200">
                      <div
                        className={cn(
                          'absolute inset-0 transition-all duration-500 ease-out',
                          index < currentStep
                            ? 'bg-gradient-to-r from-brand-500 to-brand-600 translate-x-0'
                            : 'bg-gray-200 -translate-x-full'
                        )}
                      />
                    </div>
                  )}
                </div>
                <div className="mt-3 text-center">
                  <div
                    className={cn(
                      'text-sm font-semibold transition-colors duration-300',
                      isActive
                        ? 'text-brand-600'
                        : index <= currentStep
                        ? 'text-gray-900'
                        : 'text-gray-500'
                    )}
                  >
                    {step.label}
                  </div>
                  {step.description && (
                    <div className={cn(
                      'text-xs mt-1 hidden sm:block transition-colors duration-300',
                      isActive ? 'text-brand-500 font-medium' : 'text-gray-500'
                    )}>
                      {step.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
